/**
 * WCAG 2.1 — 1.4.13 Content on Hover or Focus for Chart.js external tooltip (#chartjs-tooltip).
 * - Pointer can move onto the tooltip (pointer-events: auto; deferred hide when chart clears hover).
 * - Escape dismisses without moving pointer/focus.
 * - Canvas gets aria-describedby while the tooltip is visible.
 */

const CHART_TOOLTIP_ID = 'chartjs-tooltip';

let pointerInsideChartTooltip = false;
let deferredHideTimeout: ReturnType<typeof setTimeout> | null = null;
let escapeListenerAttached = false;

function clearDeferredHide(): void {
  if (deferredHideTimeout != null) {
    clearTimeout(deferredHideTimeout);
    deferredHideTimeout = null;
  }
}

function stripCanvasDescribedBy(tooltip: HTMLElement): void {
  const canvas = (tooltip as HTMLElement & { __ftmChartCanvas?: HTMLCanvasElement }).__ftmChartCanvas;
  if (canvas?.getAttribute('aria-describedby') === CHART_TOOLTIP_ID) {
    canvas.removeAttribute('aria-describedby');
  }
  delete (tooltip as HTMLElement & { __ftmChartCanvas?: HTMLCanvasElement }).__ftmChartCanvas;
}

/** Hide now (Escape, timer, or chart never shown). */
export function chartTooltipHideImmediately(tooltip: HTMLElement): void {
  clearDeferredHide();
  pointerInsideChartTooltip = false;
  delete tooltip.dataset.ftmTooltipOpen;
  tooltip.style.opacity = '0';
  stripCanvasDescribedBy(tooltip);
}

function onDocumentEscape(e: KeyboardEvent): void {
  if (e.key !== 'Escape') {
    return;
  }
  const tooltip = document.getElementById(CHART_TOOLTIP_ID);
  if (!tooltip) {
    return;
  }
  const opacity = parseFloat(window.getComputedStyle(tooltip).opacity);
  if (opacity < 0.01) {
    return;
  }
  e.preventDefault();
  chartTooltipHideImmediately(tooltip);
}

/**
 * Idempotent: wire pointer + Escape behavior once per tooltip element.
 */
export function attachChartTooltipWcag1413(tooltip: HTMLElement): void {
  if (tooltip.dataset.ftmWcag1413 === '1') {
    return;
  }
  tooltip.dataset.ftmWcag1413 = '1';
  tooltip.id = CHART_TOOLTIP_ID;
  tooltip.setAttribute('role', 'tooltip');
  tooltip.style.pointerEvents = 'auto';

  tooltip.addEventListener('mouseenter', () => {
    pointerInsideChartTooltip = true;
    clearDeferredHide();
  });
  tooltip.addEventListener('mouseleave', () => {
    pointerInsideChartTooltip = false;
    scheduleDeferredChartTooltipHide(tooltip);
  });

  if (!escapeListenerAttached) {
    escapeListenerAttached = true;
    document.addEventListener('keydown', onDocumentEscape);
  }
}

function scheduleDeferredChartTooltipHide(tooltip: HTMLElement): void {
  if (pointerInsideChartTooltip) {
    return;
  }
  if (deferredHideTimeout != null) {
    return;
  }
  deferredHideTimeout = setTimeout(() => {
    deferredHideTimeout = null;
    if (!pointerInsideChartTooltip) {
      chartTooltipHideImmediately(tooltip);
    }
  }, 450);
}

/** Call when Chart.js reports tooltip visible again (clears pending hide). */
export function chartTooltipOnShown(tooltip: HTMLElement, canvas: HTMLCanvasElement | null): void {
  attachChartTooltipWcag1413(tooltip);
  clearDeferredHide();
  tooltip.dataset.ftmTooltipOpen = '1';
  if (canvas) {
    (tooltip as HTMLElement & { __ftmChartCanvas?: HTMLCanvasElement }).__ftmChartCanvas = canvas;
    canvas.setAttribute('aria-describedby', CHART_TOOLTIP_ID);
  }
}

/**
 * Chart.js reports tooltipModel.opacity === 0.
 * If the tooltip was visible or the pointer is on it, keep it in the DOM briefly / until Escape.
 */
export function chartTooltipApplyChartHide(tooltip: HTMLElement): void {
  attachChartTooltipWcag1413(tooltip);
  if (pointerInsideChartTooltip) {
    tooltip.style.opacity = '1';
    return;
  }
  const wasOpen = tooltip.dataset.ftmTooltipOpen === '1';
  if (wasOpen) {
    scheduleDeferredChartTooltipHide(tooltip);
    tooltip.style.opacity = '1';
    return;
  }
  chartTooltipHideImmediately(tooltip);
}
